import type { TFunction } from 'i18next';
import { useEffect, useState, useCallback } from 'react';
import type { UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { email as zodEmail } from 'zod';

import { useCheckAdminEmail } from '@/features/api/hooks/grapevine-organization/useCheckAdminEmail';
import type {
  AdminTypeValue,
  CreateOrganizationForm
} from '@/features/profile-setting/schemas/createOrganization';
import { AdminType } from '@/features/profile-setting/schemas/createOrganization';

export type EmailStatus =
  'idle' | 'checking' | 'exists' | 'notFound' | 'invalid';

const DEBOUNCE_TIMEOUT = 600;

type Params = {
  adminType: AdminTypeValue;
  adminEmail: string | undefined;
  setError: UseFormSetError<CreateOrganizationForm>;
  clearErrors: UseFormClearErrors<CreateOrganizationForm>;
  t: TFunction<'profile-setting', 'screens.organization-create'>;
};

export const useEmailExistCheck = ({
  adminType,
  adminEmail,
  setError,
  clearErrors,
  t
}: Params) => {
  const { mutateAsync: checkAdminEmail } = useCheckAdminEmail();
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle');

  const resetEmailStatus = useCallback(() => {
    setEmailStatus('idle');
  }, []);

  const verifyAdminEmail = useDebouncedCallback(async (email: string) => {
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      setEmailStatus('idle');
      clearErrors('adminEmail');
      return;
    }
    // Pure frontend form check first — no backend request for malformed emails.
    if (!zodEmail().safeParse(trimmed).success) {
      setEmailStatus('invalid');
      setError('adminEmail', {
        type: 'validate',
        message: t('errors.adminEmailInvalid')
      });
      return;
    }
    // Syntax looks valid — now ask the backend whether the user exists.
    setEmailStatus('checking');
    // Yield one macrotask so the 'checking' render commits (and the spinner
    // paints) before the request resolves — otherwise a fast/cached response
    // resolves in a microtask and React coalesces it into a single render,
    // skipping the loading indicator entirely.
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const result = await checkAdminEmail(trimmed);
      if (result.exists) {
        clearErrors('adminEmail');
        setEmailStatus('exists');
      } else {
        setError('adminEmail', {
          type: 'server',
          message: t('errors.adminEmailNotFound')
        });
        setEmailStatus('notFound');
      }
    } catch {
      // Silently ignore network errors — schema validation still applies on submit
      setEmailStatus('idle');
    }
  }, DEBOUNCE_TIMEOUT);

  useEffect(() => {
    if (adminType !== AdminType.OTHER) {
      setEmailStatus('idle');
      clearErrors('adminEmail');
      verifyAdminEmail.cancel();
      return;
    }
    const trimmed = (adminEmail ?? '').trim();
    if (trimmed.length === 0) {
      setEmailStatus('idle');
      clearErrors('adminEmail');
      verifyAdminEmail.cancel();
      return;
    }
    // Keep the field neutral while typing; the debounced callback decides
    // whether it's a syntax error (form check) or a backend lookup.
    setEmailStatus('idle');
    verifyAdminEmail(trimmed);
  }, [adminEmail, adminType, verifyAdminEmail, clearErrors]);

  return { emailStatus, resetEmailStatus };
};
